import { create } from "zustand";
import { sendContactForm } from "@/lib/email";
import { contactFormSchema } from "@/lib/validations/contact";

interface ContactFormData {
	name: string;
	email: string;
	phone: string;
	subject: string;
	message: string;
	newsletter: boolean;
	website: string;
}

interface ContactState {
	formData: ContactFormData;
	fieldErrors: Partial<Record<keyof ContactFormData, string>>;
	isSubmitting: boolean;
	isSubmitted: boolean;
	error: string | null;
	setFormField: <K extends keyof ContactFormData>(
		field: K,
		value: ContactFormData[K],
	) => void;
	clearFieldError: (field: keyof ContactFormData) => void;
	resetForm: () => void;
	setSubmitting: (isSubmitting: boolean) => void;
	setSubmitted: (isSubmitted: boolean) => void;
	setError: (error: string | null) => void;
	submitForm: () => Promise<boolean>;
}

const defaultFormData: ContactFormData = {
	name: "",
	email: "",
	phone: "",
	subject: "",
	message: "",
	newsletter: false,
	website: "",
};

export const useContactStore = create<ContactState>((set, get) => ({
	formData: defaultFormData,
	fieldErrors: {},
	isSubmitting: false,
	isSubmitted: false,
	error: null,
	setFormField: (field, value) =>
		set((state) => ({
			formData: { ...state.formData, [field]: value },
			fieldErrors: { ...state.fieldErrors, [field]: undefined },
		})),
	clearFieldError: (field) =>
		set((state) => ({
			fieldErrors: { ...state.fieldErrors, [field]: undefined },
		})),
	resetForm: () =>
		set({
			formData: defaultFormData,
			fieldErrors: {},
			isSubmitted: false,
			error: null,
		}),
	setSubmitting: (isSubmitting) => set({ isSubmitting }),
	setSubmitted: (isSubmitted) => set({ isSubmitted }),
	setError: (error) => set({ error }),
	submitForm: async () => {
		const { formData } = get();
		set({ isSubmitting: true, error: null, fieldErrors: {} });

		const result = contactFormSchema.safeParse(formData);
		if (!result.success) {
			const fieldErrors: Partial<Record<keyof ContactFormData, string>> = {};
			for (const issue of result.error.issues) {
				const field = issue.path[0] as keyof ContactFormData;
				if (!fieldErrors[field]) {
					fieldErrors[field] = issue.message;
				}
			}
			// Field-level failures stay inline (red borders + messages under
			// each field) and block submit, the global error dialog is only
			// for actual send failures.
			set({
				fieldErrors,
				error: null,
				isSubmitting: false,
			});
			return false;
		}

		try {
			const result = await sendContactForm(formData);

			if (!result.success) {
				set({
					error: "Failed to send your message. Please try again.",
					isSubmitting: false,
				});
				return false;
			}

			set({ isSubmitted: true, isSubmitting: false });
			return true;
		} catch (error) {
			set({
				error: error instanceof Error ? error.message : "Something went wrong",
				isSubmitting: false,
			});
			return false;
		}
	},
}));
